import { Component, EventEmitter, HostBinding, Output, ViewEncapsulation } from '@angular/core';
import { MediaQueryService } from 'src/app/common/services/media.query/media.query.service';
import { ActivatedRoute, Router } from '@angular/router';
import { getRouteUrl, routeConfigs } from 'src/app/common/constants/route.config';
import { AccordionComponent } from 'src/app/common/components/accordion/accordion.component';
import { VisitorEventService } from 'src/app/modules/event-calendar/services/visitor.event.service';
import { Store } from '@ngrx/store';
import { IAppState } from 'src/app/modules/shared/ngrx.stores/frontend.settings/states';
import { VisitorMenuBarBaseComponent } from '../visitor.menu.bar.base.component';
import { ModalService } from 'src/app/common/services/modal/modal.service';
import {
  VisitorEventLayoutService,
  VisitorEventLayoutView,
} from '../../../../services/visitor.event.layout.service';

@Component({
  selector: 'ig-side-bar',
  templateUrl: './visitor.side.bar.component.html',
  styleUrls: ['./visitor.side.bar.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class VisitorSideBarComponent extends VisitorMenuBarBaseComponent {
  @HostBinding('class') hostClass =
    'bg-white overflow-y-auto overflow-x-hidden';
  @Output() toggleCollapse = new EventEmitter<void>();
  private readonly eventCardLayouts = new Set<VisitorEventLayoutView>([
    'default',
    'basic',
    'fancy',
    'square',
    'long',
    'modern-block',
    'vertical',
  ]);

  public get sideBarActiveEventLayoutStyle(): VisitorEventLayoutView {
    return this.activeEventLayoutView;
  }

  public get isCardViewActive(): boolean {
    return this.command.isEventCalendar
      ? !this.command.isMapView && this.isCardLayout(this.sideBarActiveEventLayoutStyle)
      : !this.command.isMapView;
  }

  public selectSideBarEventLayout(
    event: Event,
    layout: Extract<VisitorEventLayoutView, 'calendar' | 'list'>,
    accordion: AccordionComponent
  ) {
    this.stopSideBarMenuEvent(event);
    if (!this.command.isEventCalendar) return;

    if (!this.command.isMapView && this.sideBarActiveEventLayoutStyle === layout) {
      accordion.closeMenu();
      this.close();
      return;
    }

    this.visitorEventLayoutService.setSelectedLayout(layout);

    if (this.command.isMapView) {
      this.command.isMapView = false;
      this._router.navigateByUrl(
        getRouteUrl(routeConfigs.visitor.eventCalendar, {
          hubName: this.hubName,
        }),
        { skipLocationChange: this.noChangeUrlWhenRedirect }
      );
    }

    accordion.closeMenu();
    this.close();
  }

  public selectSideBarCardView(event: Event, accordion: AccordionComponent) {
    this.stopSideBarMenuEvent(event);

    if (this.isCardViewActive) {
      accordion.closeMenu();
      this.close();
      return;
    }

    if (this.command.isEventCalendar) {
      this.visitorEventLayoutService.setSelectedLayout(this.getPreferredCardLayout());
    }

    this.command.isMapView = false;
    this._router.navigateByUrl(
      getRouteUrl(
        this.command.isEventCalendar
          ? routeConfigs.visitor.eventCalendar
          : routeConfigs.visitor.placeCalendar,
        {
          hubName: this.hubName,
        }
      ),
      { skipLocationChange: this.noChangeUrlWhenRedirect }
    );

    accordion.closeMenu();
    this.close();
  }

  public selectSideBarMapView(event: Event, accordion: AccordionComponent) {
    this.stopSideBarMenuEvent(event);
    if (this.command.isMapView) {
      accordion.closeMenu();
      this.close();
      return;
    }

    this.command.isMapView = true;
    const mapRoute = this.command.isEventCalendar
      ? routeConfigs.visitor.eventCalendar.eventMap
      : routeConfigs.visitor.placeCalendar.placeMap;
    this._router.navigateByUrl(
      getRouteUrl(mapRoute, {
        hubName: this.hubName,
      }),
      { skipLocationChange: this.noChangeUrlWhenRedirect }
    );

    accordion.closeMenu();
    this.close();
  }

  private stopSideBarMenuEvent(event: Event) {
    event.preventDefault();
    event.stopPropagation();
  }

  private getPreferredCardLayout(): VisitorEventLayoutView {
    const configuredLayout = this.defaultEventLayoutView;

    return this.isCardLayout(configuredLayout) ? 'default' : 'fancy';
  }

  private isCardLayout(layout: VisitorEventLayoutView | undefined | null): boolean {
    return !!layout && this.eventCardLayouts.has(layout);
  }

  constructor(
    public override activatedRoute: ActivatedRoute,
    public override mediaQueryService: MediaQueryService,
    public override eventService: VisitorEventService,
    public override modalService: ModalService,
    protected override visitorEventLayoutService: VisitorEventLayoutService,
    protected override _router: Router,
    protected override _store: Store<IAppState>
  ) {
    super(
      activatedRoute,
      mediaQueryService,
      eventService,
      modalService,
      visitorEventLayoutService,
      _router,
      _store
    );
  }

  onToggleCollapse() {
    this.toggleCollapse.emit();
  }
}
